import { motion, useAnimationFrame, useMotionValue } from "framer-motion";
import { useMemo, useState } from "react";

const Testimonials = () => {
  const [isPaused, setIsPaused] = useState(false);
  const CARD_WIDTH = 320;
  const CARD_GAP = 24;
  const SCROLL_SPEED = 118; // pixels per second

  const testimonials = useMemo(() => [
    {
      text: "Sera has truly transformed my productivity. I can finally focus without constant interruptions while still staying connected to what matters.",
      author: "Emma Johnson",
      role: "Product Manager"
    },
    {
      text: "The smart batching feature is incredible. I get all my notifications when I'm ready to handle them, not when they interrupt my flow.",
      author: "David Wilson",
      role: "Founder"
    },
    {
      text: "Finally, an app that respects my time and attention. Sera's privacy-first approach gives me peace of mind while staying productive.",
      author: "Sarah Chen",
      role: "Designer"
    },
    {
      text: "I've tried many productivity apps, but Sera is the only one that actually helps me stay focused. The batching is genius!",
      author: "Michael Rodriguez",
      role: "Software Engineer"
    },
    {
      text: "As a busy parent, Sera helps me manage my notifications without missing important messages from my family.",
      author: "Lisa Thompson",
      role: "Marketing Director"
    },
    {
      text: "The customization options are perfect. I can set exactly when I want to receive my notifications.",
      author: "Alex Kim",
      role: "Freelance Designer"
    },
    {
      text: "As a college student, I use Sera during study blocks so group chats don't derail me before exams.",
      author: "Maya Patel",
      role: "University Student"
    },
    {
      text: "Sera helps me separate social pings from urgent school updates. My homework time is way more focused now.",
      author: "Jordan Lee",
      role: "High School Student"
    },
    {
      text: "With toddlers at home, my phone used to feel nonstop. Now I check batches during nap time and stay calmer.",
      author: "Ashley Turner",
      role: "Stay-at-Home Mom"
    },
    {
      text: "I still get calls from family instantly, but everything else waits until I have a moment. Huge stress reduction.",
      author: "Rebecca Moore",
      role: "Stay-at-Home Parent"
    },
    {
      text: "I'm in the classroom all day, and Sera keeps my phone quiet without missing district alerts.",
      author: "Daniel Brooks",
      role: "Middle School Teacher"
    },
    {
      text: "Between grading and lesson planning, batched notifications help me protect deep work time every evening.",
      author: "Priya Nair",
      role: "High School Teacher"
    },
    {
      text: "As a nurse on rotating shifts, I can change delivery windows quickly and avoid unnecessary distractions.",
      author: "Carlos Mendez",
      role: "Registered Nurse"
    },
    {
      text: "I drive most of the day, so fewer interruptions matter. Sera gives me updates when I'm safely parked.",
      author: "Ethan Clark",
      role: "Delivery Driver"
    },
    {
      text: "The app helps me focus on client sessions without buzzing distractions, then catch up between appointments.",
      author: "Nina Shah",
      role: "Therapist"
    },
    {
      text: "I run a small shop and wear every hat. Sera lets VIP customer messages through but batches the rest.",
      author: "Olivia Grant",
      role: "Small Business Owner"
    },
    {
      text: "Remote work is full of pings. Sera made my mornings noticeably calmer and my output more consistent.",
      author: "Ben Harris",
      role: "Customer Support Specialist"
    },
    {
      text: "I care for my dad at home, and this gives me one less thing to juggle while still staying reachable.",
      author: "Sofia Ramirez",
      role: "Family Caregiver"
    }
  ], []);

  const shuffledTestimonials = useMemo(() => {
    const shuffled = [...testimonials];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, [testimonials]);

  // Duplicate testimonials for seamless loop
  const duplicatedTestimonials = [...shuffledTestimonials, ...shuffledTestimonials];
  const scrollDistance = shuffledTestimonials.length * (CARD_WIDTH + CARD_GAP);
  const x = useMotionValue(0);

  useAnimationFrame((_, delta) => {
    if (isPaused) {
      return;
    }

    const moveBy = (SCROLL_SPEED * delta) / 1000;
    let nextX = x.get() - moveBy;

    if (nextX <= -scrollDistance) {
      nextX += scrollDistance;
    }

    x.set(nextX);
  });

  return (
    <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Our Testimonials
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            See how Sera has transformed users' productivity through their own words.
          </p>
        </div>

        {/* Moving Carousel */}
        <div className="relative overflow-hidden">
          <motion.div
            className="flex space-x-6"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            style={{
              x,
              width: "200%", // Double width to accommodate duplicated testimonials
            }}
          >
            {duplicatedTestimonials.map((testimonial, index) => (
              <motion.div
                key={`${testimonial.author}-${index}`}
                className="flex-shrink-0 w-80 bg-slate-800/50 rounded-2xl p-6"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-400 font-semibold text-sm">
                      {testimonial.author.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-slate-300 mb-4 italic text-sm leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div>
                      <h4 className="font-semibold text-white text-sm">{testimonial.author}</h4>
                      <p className="text-xs text-slate-400">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Gradient Overlays for smooth edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-800/30 to-transparent pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-800/30 to-transparent pointer-events-none"></div>
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
